import { useCallback, useEffect, useState } from "react";
import { AutoPlayStatus, PromoState } from "../../../globals";
import { useIntersectionContext } from "../../contexts/IntersectionContext/IntersectionProvider";
import { useStickyVideoContext } from "../../contexts/StickyVideoContext";

export const useInitializeNewVideo = ({
  setPromoState,
  updateAutoplayState,
  shouldShowAds,
  autoplayState,
  isOrcaInitialized,
  isCarousel
}) => {
  const { isInViewPort } = useIntersectionContext();
  const { isOpenSticky } = useStickyVideoContext();
  const [waitForOrcaInitialized, setWaitForOrcaInitialized] = useState(null);
  const [waitForOrcaViewPort, setWaitForOrcaViewPort] = useState(null);

  // setup player for a new video
  const initializeNewVideo = useCallback(
    (videoWillAutoplay, overrideReducedMotion) => {
      // Wait for Orca to be initialized before setting up the video
      if (!isOrcaInitialized) {
        setWaitForOrcaInitialized({ videoWillAutoplay, overrideReducedMotion });
        return;
      }

      if (!videoWillAutoplay) {
        setPromoState(PromoState.READY);
        return;
      }

      if (!isInViewPort && !isCarousel && !isOpenSticky) {
        setWaitForOrcaViewPort({
          videoWillAutoplay,
          overrideReducedMotion
        });
        return;
      }
      updateAutoplayState(
        { status: AutoPlayStatus.INIT },
        overrideReducedMotion
      );

      if (autoplayState.isLivePreview) {
        return;
      }

      setPromoState(shouldShowAds ? PromoState.BUFFER : PromoState.HIDDEN);
    },
    [
      setPromoState,
      updateAutoplayState,
      shouldShowAds,
      autoplayState.isLivePreview,
      isOrcaInitialized,
      isInViewPort,
      isCarousel,
      isOpenSticky
    ]
  );

  useEffect(() => {
    // If we were waiting for Orca to initialize, do it now that it is ready
    if (waitForOrcaInitialized && isOrcaInitialized) {
      initializeNewVideo(
        waitForOrcaInitialized.videoWillAutoplay,
        waitForOrcaInitialized.overrideReducedMotion
      );
      setWaitForOrcaInitialized(null);
    }
  }, [isOrcaInitialized, initializeNewVideo, waitForOrcaInitialized]);

  useEffect(() => {
    // If we were waiting for Orca to enter view port, iniitalize once it enters
    if (waitForOrcaViewPort && isInViewPort) {
      initializeNewVideo(
        waitForOrcaViewPort.videoWillAutoplay,
        waitForOrcaViewPort.overrideReducedMotion
      );
      setWaitForOrcaViewPort(null);
    }
  }, [isInViewPort, initializeNewVideo, waitForOrcaViewPort]);

  return initializeNewVideo;
};
